import {
  createFileRoute,
  useSearch,
  useNavigate,
} from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { clientInvoicesService } from '@/features/client-invoices/services/client-invoices.service';
import type {
  ClientInvoice,
  CreateClientInvoiceDto,
  UpdateClientInvoiceDto,
} from '@/features/client-invoices/services/client-invoices.service';
import { CreateInvoiceForm } from '@/features/client-invoices/components/CreateInvoiceForm';
import { RegisterPaymentForm } from '@/features/client-invoices/components/RegisterPaymentForm';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  DollarSign,
  Upload,
  Download,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function toMonthParam(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(month: string) {
  const [y, m] = month.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} de ${y}`;
}

export const Route = createFileRoute('/_authenticated/client-invoices/')({
  validateSearch: (search: Record<string, unknown>): { clientId?: string; month?: string } => ({
    clientId: (search.clientId as string) || undefined,
    month: (search.month as string) || undefined,
  }),
  component: ClientInvoicesPage,
});

function ClientInvoicesPage() {
  const navigate = useNavigate();
  const { clientId: clientIdFromUrl, month: monthFromUrl } = useSearch({
    from: '/_authenticated/client-invoices/',
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [month, setMonth] = useState(() => monthFromUrl || toMonthParam(new Date()));
  const [searchTerm, setSearchTerm] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [preselectedClientId, setPreselectedClientId] = useState<string | undefined>(undefined);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<ClientInvoice | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  useEffect(() => {
    if (monthFromUrl) setMonth(monthFromUrl);
  }, [monthFromUrl]);

  useEffect(() => {
    if (clientIdFromUrl) {
      setPreselectedClientId(clientIdFromUrl);
      setCreateOpen(true);
    }
  }, [clientIdFromUrl]);

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['client-invoices-dashboard', month, searchTerm, includeCancelled],
    queryFn: () => clientInvoicesService.getDashboard(month, searchTerm || undefined, includeCancelled),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateClientInvoiceDto) =>
      clientInvoicesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-invoices-dashboard'] });
      toast({ title: 'Fatura criada com sucesso' });
      setCreateOpen(false);
      setPreselectedClientId(undefined);
      if (clientIdFromUrl) navigate({ to: '/client-invoices', search: { clientId: undefined, month: undefined } });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientInvoiceDto }) =>
      clientInvoicesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-invoices-dashboard'] });
      toast({ title: 'Fatura atualizada' });
      setPaymentOpen(false);
      setSelectedInvoice(null);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      clientInvoicesService.uploadFile(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-invoices-dashboard'] });
      toast({ title: 'Comprovante enviado' });
      setUploadOpen(false);
      setSelectedInvoice(null);
      setUploadFile(null);
    },
  });

  const handleDownload = async (invoice: ClientInvoice) => {
    try {
      const { url } = await clientInvoicesService.getDownloadUrl(invoice.id);
      window.open(url, '_blank');
    } catch {
      // o interceptor do axios já mostra o toast de erro
    }
  };

  const goPrevMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    const next = toMonthParam(d);
    setMonth(next);
    navigate({ to: '/client-invoices', search: { clientId: clientIdFromUrl, month: next } });
  };

  const goNextMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m, 1);
    const next = toMonthParam(d);
    setMonth(next);
    navigate({ to: '/client-invoices', search: { clientId: clientIdFromUrl, month: next } });
  };

  const formatCurrency = (v: number) =>
    v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const invoices = dashboard?.invoices ?? [];
  const [year, monthNum] = month.split('-').map(Number);
  const firstDay = new Date(year, monthNum - 1, 1);
  const lastDay = new Date(year, monthNum, 0);

  // Filtrar invoices baseado no statusFilter
  const filteredInvoices = statusFilter
    ? invoices.filter(({ invoice }) => {
        const today = new Date().toISOString().slice(0, 10);
        switch (statusFilter) {
          case 'overdue':
            return invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.dueDate < today;
          case 'dueToday':
            return invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.dueDate === today;
          case 'toReceive':
            return invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.dueDate > today;
          case 'received':
            return invoice.status === 'paid';
          default:
            return true;
        }
      })
    : invoices;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Contas a receber</h1>
          <p className="text-muted-foreground">
            Dashboard de faturas do mês. Valores de entrada, em atraso e a vencer.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nova fatura
        </Button>
      </div>

      {/* Navegação por mês e pesquisa */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goPrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-lg font-medium min-w-[180px] text-center">
                {formatMonthLabel(month)}
              </span>
              <Button variant="outline" size="icon" onClick={goNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar por nome da fatura ou do cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeCancelled"
                checked={includeCancelled}
                onCheckedChange={(checked) => setIncludeCancelled(checked === true)}
              />
              <Label htmlFor="includeCancelled" className="text-sm cursor-pointer">
                Mostrar canceladas
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de resumo */}
      {dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            <Card
              className={cn(
                'border-red-200 bg-red-50/50 dark:bg-red-950/20 cursor-pointer hover:shadow-md transition-all',
                statusFilter === 'overdue' && 'ring-2 ring-red-500 shadow-md',
              )}
              onClick={() => setStatusFilter(statusFilter === 'overdue' ? null : 'overdue')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-700 dark:text-red-400">
                  Vencidos (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-xl font-bold text-red-700 dark:text-red-300">
                  {formatCurrency(dashboard.overdue)}
                </span>
              </CardContent>
            </Card>
            <Card
              className={cn(
                'border-orange-200 bg-orange-50/50 dark:bg-orange-950/20 cursor-pointer hover:shadow-md transition-all',
                statusFilter === 'dueToday' && 'ring-2 ring-orange-500 shadow-md',
              )}
              onClick={() => setStatusFilter(statusFilter === 'dueToday' ? null : 'dueToday')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-orange-700 dark:text-orange-400">
                  Vencem hoje (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-xl font-bold text-orange-700 dark:text-orange-300">
                  {formatCurrency(dashboard.dueToday)}
                </span>
              </CardContent>
            </Card>
            <Card
              className={cn(
                'border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 cursor-pointer hover:shadow-md transition-all',
                statusFilter === 'toReceive' && 'ring-2 ring-blue-500 shadow-md',
              )}
              onClick={() => setStatusFilter(statusFilter === 'toReceive' ? null : 'toReceive')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-400">
                  A vencer (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-xl font-bold text-blue-700 dark:text-blue-300">
                  {formatCurrency(dashboard.toReceive)}
                </span>
              </CardContent>
            </Card>
            <Card
              className={cn(
                'border-green-200 bg-green-50/50 dark:bg-green-950/20 cursor-pointer hover:shadow-md transition-all',
                statusFilter === 'received' && 'ring-2 ring-green-500 shadow-md',
              )}
              onClick={() => setStatusFilter(statusFilter === 'received' ? null : 'received')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-green-700 dark:text-green-400">
                  Recebidos (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-xl font-bold text-green-700 dark:text-green-300">
                  {formatCurrency(dashboard.received)}
                </span>
              </CardContent>
            </Card>
            <Card className="border-muted">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total do período (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-xl font-bold">
                  {formatCurrency(dashboard.total)}
                </span>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-amber-700 dark:text-amber-400">
                  Atrasados mês ant. (R$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-xl font-bold text-amber-700 dark:text-amber-300">
                  {formatCurrency(dashboard.previousMonthOverdue)}
                </span>
              </CardContent>
            </Card>
          </div>
          {statusFilter && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatusFilter(null)}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Limpar filtro
              </Button>
              <span className="text-sm text-muted-foreground">
                Mostrando {filteredInvoices.length} de {invoices.length} faturas
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tabela de faturas */}
      <Card>
        <CardHeader>
          <CardTitle>Lançamentos do período</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Carregando...
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {statusFilter
                ? 'Nenhuma fatura encontrada com o filtro selecionado.'
                : 'Nenhuma fatura no período.'}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">Vencimento</th>
                      <th className="text-left p-3 font-medium">Recebimento</th>
                      <th className="text-left p-3 font-medium">Cliente</th>
                      <th className="text-left p-3 font-medium">Descrição</th>
                      <th className="text-right p-3 font-medium">Total (R$)</th>
                      <th className="text-right p-3 font-medium">A receber</th>
                      <th className="p-3 font-medium">Situação</th>
                      <th className="p-3 font-medium w-[140px]">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map(({ invoice, client }) => {
                      const val = parseFloat(invoice.value);
                      const isPaid = invoice.status === 'paid';
                      const aReceber = isPaid ? 0 : val;
                      return (
                        <tr key={invoice.id} className="border-b">
                          <td className="p-3">
                            {format(new Date(invoice.dueDate), 'dd/MM/yyyy', {
                              locale: ptBR,
                            })}
                          </td>
                          <td className="p-3">
                            {invoice.paymentDate
                              ? format(new Date(invoice.paymentDate), 'dd/MM/yyyy', {
                                  locale: ptBR,
                                })
                              : '–'}
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{client.name}</div>
                            {client.tradeName && (
                              <div className="text-sm text-muted-foreground">
                                {client.tradeName}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="font-medium">
                              {invoice.name || `${invoice.referenceMonth}`}
                            </div>
                            {invoice.paymentNotes && (
                              <div className="text-sm text-muted-foreground">
                                {invoice.paymentNotes}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            {formatCurrency(val)}
                          </td>
                          <td className="p-3 text-right">
                            {aReceber > 0 ? formatCurrency(aReceber) : '0,00'}
                          </td>
                          <td className="p-3">
                            <Badge
                              variant={
                                invoice.status === 'paid'
                                  ? 'default'
                                  : invoice.status === 'overdue'
                                    ? 'destructive'
                                    : 'secondary'
                              }
                            >
                              {invoice.status === 'paid'
                                ? 'Recebido'
                                : invoice.status === 'overdue'
                                  ? 'Vencido'
                                  : invoice.status === 'cancelled'
                                    ? 'Cancelado'
                                    : 'Em aberto'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Registrar pagamento"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setPaymentOpen(true);
                                }}
                              >
                                <DollarSign className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Enviar comprovante"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setUploadFile(null);
                                  setUploadOpen(true);
                                }}
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                              {invoice.invoiceStorageKey && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Download"
                                  onClick={() => handleDownload(invoice)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end border-t py-3 mt-2">
                <p className="text-sm text-muted-foreground">
                  Totais do período ({format(firstDay, 'dd/MM/yyyy', { locale: ptBR })} a{' '}
                  {format(lastDay, 'dd/MM/yyyy', { locale: ptBR })}):{' '}
                  <span className="font-semibold">
                    R$ {dashboard ? formatCurrency(dashboard.total) : '0,00'}
                  </span>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nova fatura */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setPreselectedClientId(undefined);
        }}
      >
        <DialogContent size="md" busy={createMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Nova fatura</DialogTitle>
          </DialogHeader>
          <CreateInvoiceForm
            key={preselectedClientId ?? 'create'}
            preselectedClientId={preselectedClientId}
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setCreateOpen(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog Registrar pagamento */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent size="md" busy={updateMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Registrar pagamento</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <RegisterPaymentForm
              invoice={selectedInvoice}
              onSubmit={(data) =>
                updateMutation.mutate({ id: selectedInvoice.id, data })
              }
              onCancel={() => {
                setPaymentOpen(false);
                setSelectedInvoice(null);
              }}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Upload comprovante */}
      <Dialog
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) setUploadFile(null);
        }}
      >
        <DialogContent size="sm" busy={uploadMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Enviar comprovante</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (uploadFile)
                  uploadMutation.mutate({ id: selectedInvoice.id, file: uploadFile });
              }}
            >
              <p className="text-sm text-muted-foreground">
                {selectedInvoice.name || selectedInvoice.referenceMonth} – R${' '}
                {parseFloat(selectedInvoice.value).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })}
              </p>
              <div className="space-y-2">
                <label htmlFor="invoice-proof" className="text-sm font-medium">Arquivo (PDF ou imagem) *</label>
                <input
                  id="invoice-proof"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="block w-full text-sm"
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUploadOpen(false);
                    setSelectedInvoice(null);
                    setUploadFile(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={!uploadFile} loading={uploadMutation.isPending}>
                  Enviar comprovante
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
